// components/FutureLookButton.tsx
import { useState, useEffect } from 'react';
import { Wand2, Lock, Camera, Sparkles, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import FutureLookModal from './FutureLookModal';

interface FutureLookButtonProps {
  protocolGenerated: boolean;
  onboardingCompleted: boolean;
  onLockedClick: () => void;
}

export default function FutureLookButton({
  protocolGenerated,
  onboardingCompleted,
  onLockedClick,
}: FutureLookButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [canUseFeature, setCanUseFeature] = useState(true);
  const [hasExistingTransformation, setHasExistingTransformation] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkFeatureAvailability();
  }, [protocolGenerated]);

  const checkFeatureAvailability = async () => {
    if (!protocolGenerated) {
      setCanUseFeature(false);
      setIsLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's protocol with generation_date
      const { data: protocol } = await supabase
        .from('user_protocols')
        .select('id, generation_date')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!protocol || !protocol.generation_date) {
        setCanUseFeature(false);
        setIsLoading(false);
        return;
      }

      // Check if transformation exists for this protocol generation
      const { data: transformation } = await supabase
        .from('future_look_transformations')
        .select('id, generation_status')
        .eq('user_id', user.id)
        .eq('protocol_id', protocol.id)
        .eq('protocol_generation_date', protocol.generation_date)
        .maybeSingle();

      if (transformation) {
        if (transformation.generation_status === 'completed') {
          setCanUseFeature(false);
          setHasExistingTransformation(true);
        } else if (transformation.generation_status === 'failed') {
          setCanUseFeature(true);
          setHasExistingTransformation(false);
        }
      } else {
        setCanUseFeature(true);
        setHasExistingTransformation(false);
      }
    } catch (error) {
      console.error('Error checking feature availability:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (!onboardingCompleted) {
      onLockedClick();
      return;
    }

    if (!protocolGenerated) {
      // Could show a toast: "Generate a protocol first"
      return;
    }

    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    // Refresh availability after modal closes
    checkFeatureAvailability();
  };

  // Determine button state
  const isLocked = !onboardingCompleted || !protocolGenerated;
  const isUsed = hasExistingTransformation && !canUseFeature;

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`w-full bg-white rounded-2xl p-4 flex items-center justify-between border border-gray-200 hover:border-gray-300 transition-all active:scale-[0.99] ${
          isLocked ? 'opacity-60' : ''
        } ${isUsed ? 'border-purple-200 bg-purple-50/50' : ''}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isUsed 
              ? 'bg-purple-100' 
              : isLocked 
                ? 'bg-gray-100' 
                : 'bg-gradient-to-br from-violet-100 to-fuchsia-100'
          }`}>
            {isLocked ? (
              <Lock className="w-5 h-5 text-gray-400" strokeWidth={2} />
            ) : isUsed ? (
              <Sparkles className="w-5 h-5 text-purple-600" strokeWidth={2} />
            ) : (
              <Wand2 className="w-5 h-5 text-violet-600" strokeWidth={2} />
            )}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold text-sm ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>
                {isUsed ? 'View Result' : 'Future Look'}
              </h3>
              {!isLocked && !isUsed && (
                <span className="px-1.5 py-0.5 bg-violet-100 rounded-full text-[9px] font-bold text-violet-600 uppercase">
                  AI
                </span>
              )}
              {isUsed && (
                <span className="px-1.5 py-0.5 bg-green-100 rounded-full text-[9px] font-bold text-green-600 uppercase">
                  Done
                </span>
              )}
            </div>
            <p className={`text-xs ${isLocked ? 'text-gray-300' : 'text-gray-500'}`}>
              {isLocked 
                ? 'Complete profile first' 
                : isUsed 
                  ? 'See your transformation'
                  : 'See your future self'
              }
            </p>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${
          isLocked ? 'text-gray-300' : 'text-gray-400'
        }`} strokeWidth={2} />
      </button>

      {showModal && (
        <FutureLookModal
          onClose={handleModalClose}
          hasExistingTransformation={hasExistingTransformation}
        />
      )}
    </>
  );
}